import { isDevelopment } from '../config/app'

export enum LogLevel {
    ERROR = 0,
    WARN = 1,
    INFO = 2,
    DEBUG = 3,
}

class Logger {
    private level: LogLevel

    constructor(level: LogLevel = LogLevel.INFO) {
        this.level = level
    }

    private shouldLog(level: LogLevel): boolean {
        return level <= this.level
    }

    private formatMessage(level: string, message: string, meta?: unknown): void {
        const timestamp = new Date().toISOString()
        const prefix = `[${timestamp}] [${level}]`

        if (meta) {
            // Use console methods allowed by ESLint
            if (level === 'ERROR') {
                console.error(`${prefix} ${message}`, meta)
            } else if (level === 'WARN') {
                console.warn(`${prefix} ${message}`, meta)
            } else {
                // For INFO and DEBUG, we'll use console.log
                console.log(`${prefix} ${message}`, meta)
            }
        } else {
            if (level === 'ERROR') {
                console.error(`${prefix} ${message}`)
            } else if (level === 'WARN') {
                console.warn(`${prefix} ${message}`)
            } else {
                // For INFO and DEBUG, we'll use console.log
                console.log(`${prefix} ${message}`)
            }
        }
    }

    error(message: string, meta?: unknown): void {
        if (this.shouldLog(LogLevel.ERROR)) {
            this.formatMessage('ERROR', message, meta)

            // In production, you'd send this to your error reporting service
            if (!isDevelopment) {
                // Example: Sentry.captureMessage(message, 'error');
            }
        }
    }

    warn(message: string, meta?: unknown): void {
        if (this.shouldLog(LogLevel.WARN)) {
            this.formatMessage('WARN', message, meta)
        }
    }

    info(message: string, meta?: unknown): void {
        if (this.shouldLog(LogLevel.INFO)) {
            this.formatMessage('INFO', message, meta)
        }
    }

    debug(message: string, meta?: unknown): void {
        if (this.shouldLog(LogLevel.DEBUG) && isDevelopment) {
            this.formatMessage('DEBUG', message, meta)
        }
    }

    // Helper methods for common scenarios
    apiRequest(url: string, method: string, data?: unknown): void {
        this.debug(`API ${method} ${url}`, data)
    }

    apiResponse(url: string, status: number, data?: unknown): void {
        this.debug(`API Response ${status} ${url}`, data)
    }

    userAction(action: string, data?: unknown): void {
        this.info(`User Action: ${action}`, data)
    }

    navigation(from: string, to: string): void {
        this.debug(`Navigation: ${from} → ${to}`)
    }
}

// Create singleton instance
export const logger = new Logger(isDevelopment ? LogLevel.DEBUG : LogLevel.INFO)
